import {Component, Input, OnInit} from '@angular/core';
import {EventsService} from '../../../../../../../../../../@shared/services/events-service';
import {_AQH_EVE_2, _AQH_EVE_5, AQH_EVE_4} from '../../../../../../../../../../@shared/models/input.module';
import {
  CompanyResponse,
  EventResponse,
  EventTypeResponse,
  CountryResponse
} from '../../../../../../../../../../@shared/models/response.module';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastService} from '../../../../../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {StaticService} from '../../../../../../../../../../@shared/services/static-service';

@Component({
  selector: 'app-edit-event',
  standalone: false,
  templateUrl: './edit-event.html',
  styleUrl: './edit-event.css'
})
export class EditEvent implements OnInit {
  @Input() company!: CompanyResponse;
  @Input() event!: EventResponse;

  eventTypes: EventTypeResponse[] = [];
  countries: CountryResponse[] = [];

  event_name = '';
  event_description = '';
  event_type_id!: EventTypeResponse | null;
  discipline: string = '';
  start_date: string | null = null;
  end_date: string | null = null;
  address = '';
  postal_code = '';
  city = '';
  country_id: number | null = null;
  registration_deadline: string | null = null;

  logo?: File;
  header?: File;

  saving = false;
  submitted = false;

  disciplineOptions = [
    { value: 'distance', label: 'Distanse' },
    { value: 'dressage', label: 'Dressur' },
    { value: 'eventing', label: 'Feltritt' },
    { value: 'icelandic', label: 'Islandshest' },
    { value: 'driving', label: 'Kjøring' },
    { value: 'mounted_games', label: 'Mounted Games' },
    { value: 'para_dressage', label: 'Paradressur' },
    { value: 'jumping', label: 'Sprang' },
    { value: 'vaulting', label: 'Voltige' },
    { value: 'western', label: 'Western' },
    { value: 'barrel_racing', label: 'Barrel Racing' },
    { value: 'pole_bending', label: 'Pole Bending' },
    { value: 'reining', label: 'Reining' },
    { value: 'working_eq', label: 'Working Equitation' },
    { value: 'skeid', label: 'Skeid' },
    { value: 'equestrian', label: 'Riddesport' },
    { value: 'trotting', label: 'Trav' },
    { value: 'gallop', label: 'Galopp' },
    { value: 'polo', label: 'Polo' },
    { value: 'academic', label: 'Akademisk Ridekunst' },
    { value: 'exhibition', label: 'Utstilling' },
    { value: 'struk', label: 'STRUK' },
    { value: 'other', label: 'Annet' }
  ];

  constructor(private eventService: EventsService,
              private modal: NgbActiveModal,
              private toastService: ToastService,
              private translateService: TranslateService,
              private staticService: StaticService) {}

  // Validation helpers
  get isEventNameInvalid(): boolean {
    return this.submitted && !this.event_name?.trim();
  }

  get isEventTypeInvalid(): boolean {
    return this.submitted && !this.event_type_id;
  }

  get isDisciplineInvalid(): boolean {
    // Discipline is required for competition and training events, optional for social
    if (!this.submitted) return false;
    if (this.isSocialEvent) return false;
    return !this.discipline;
  }

  get isDisciplineRequired(): boolean {
    return !this.isSocialEvent;
  }

  get isSocialEvent(): boolean {
    return this.event?.category === 'social';
  }

  get isStartDateInvalid(): boolean {
    return this.submitted && !this.start_date;
  }

  get isFormValid(): boolean {
    const isDisciplineValid = this.isSocialEvent || !!this.discipline;
    return !!this.event_name?.trim() && !!this.event_type_id && !!this.start_date && isDisciplineValid;
  }

  ngOnInit() {
    this.getEventTypes();
    this.loadCountries();
  }

  loadCountries() {
    this.staticService.getCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
      },
      error: (err) => {
        console.error('Failed to load countries', err);
      }
    });
  }

  private getEventTypes() {
    const params: AQH_EVE_4 = {};
    this.eventService.getEventTypes(params).subscribe((res) => {
      this.eventTypes = res || [];
      this.patchFromEvent();
    });
  }

  private toInputLocal(dt: Date | string | null | undefined): string {
    if (!dt) return '';

    // Already correct format
    if (typeof dt === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dt)) {
      return dt;
    }

    // 1) Handle "YYYY-MM-DD HH:mm[:ss]"
    if (typeof dt === 'string') {
      const s = dt.trim();

      const ymd = s.replace('T', ' ').match(
        /^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2})(?::(\d{2}))?$/
      );
      if (ymd) {
        const [, yyyy, mm, dd, hh, mi] = ymd;
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      }

      // 2) Handle "dd/mm/yyyy, HH:mm"
      const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})$/);
      if (dmy) {
        let [, dd, mm, yyyy, hh, mi] = dmy;
        dd = dd.padStart(2, '0');
        mm = mm.padStart(2, '0');
        hh = hh.padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      }

      // 3) Handle "mm/dd/yyyy, HH:mm" (US ordering)
      const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
      if (mdy) {
        let [, mm, dd, yyyy, hh, mi, ampm] = mdy;
        let hour = parseInt(hh, 10);
        if (ampm) {
          const isPM = ampm.toUpperCase() === 'PM';
          if (isPM && hour < 12) hour += 12;
          if (!isPM && hour === 12) hour = 0;
        }
        dd = dd.padStart(2, '0');
        mm = mm.padStart(2, '0');
        const hh2 = String(hour).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh2}:${mi}`;
      }

      // 4) Last resort: native Date parse
      const nd = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
      if (!isNaN(nd.getTime())) {
        const yyyy = nd.getFullYear();
        const mm = String(nd.getMonth() + 1).padStart(2, '0');
        const dd = String(nd.getDate()).padStart(2, '0');
        const hh = String(nd.getHours()).padStart(2, '0');
        const mi = String(nd.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      }

      return '';
    }

    // Date instance (already local): format without TZ conversion
    const d = dt as Date;
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }

  private patchFromEvent() {
    if (!this.event) return;

    this.event_name = this.event.event_name || '';
    this.event_description = this.event.event_description || '';
    this.discipline = this.event.discipline || '';
    this.address = this.event.address || '';
    this.postal_code = this.event.postal_code || '';
    this.city = this.event.city || '';
    this.country_id = this.event.country_id || null;

    this.start_date = this.toInputLocal(this.event.start_date as any);
    this.end_date = this.toInputLocal(this.event.end_date as any);
    this.registration_deadline = this.toInputLocal(this.event.registration_deadline as any);

    this.event_type_id =
      this.eventTypes.find(t => t.event_type_id === this.event.event_type_id) || null;
  }

  save() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('manageEvent.editEvent.validationError'));
      return;
    }

    if (!this.event?.event_id) return;
    this.saving = true;

    const params: _AQH_EVE_2 = {
      event_id: this.event.event_id,
      event_name: this.event_name,
      event_description: this.event_description,
      event_type_id: this.event_type_id!.event_type_id,
      discipline: this.discipline || undefined,
      start_date: this.start_date || '',
      end_date: this.end_date || '',
      address: this.address,
      postal_code: this.postal_code,
      city: this.city,
      country: '',  // Legacy field - we now use country_id
      country_id: this.country_id,
      registration_deadline: this.registration_deadline || '',
    };

    this.eventService.editEvent(params).subscribe((res) => {
      const updated = res.data;

      const uploads: Array<Promise<any>> = [];

      if (this.logo) {
        const logoParams: _AQH_EVE_5 = {
          event_id: updated.event_id,
          image_type: 'logo',
          image: this.logo
        };
        uploads.push(
          this.eventService.editEventFiles(logoParams).toPromise()
        );
      }

      if (this.header) {
        const headerParams: _AQH_EVE_5 = {
          event_id: updated.event_id,
          image_type: 'header',
          image: this.header
        };
        uploads.push(
          this.eventService.editEventFiles(headerParams).toPromise()
        );
      }

      if (uploads.length) {
        Promise.allSettled(uploads).finally(() => {
          this.saving = false;
          this.toastService.success(this.translateService.instant('manageEvent.editEvent.success'));
          this.modal.close(true);
        });
      } else {
        this.saving = false;
        this.toastService.success(this.translateService.instant('manageEvent.editEvent.success'));
        this.modal.close(true);
      }
    }, (err) => {
      this.saving = false;
      this.toastService.error(err?.error?.reason || this.translateService.instant('manageEvent.editEvent.error'));
    });
  }

  close() {
    this.modal.close(false)
  }
}
